import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

import { showSnackbar } from 'helpers/snackbar';

import { media } from 'context';
import { Icon, Layout, PillButton, Text } from 'ds';
import { formatUsd } from 'helpers';
import { CLEANING_SERVICE_PATH } from 'routes/paths';
import { formatDate } from 'shared/time';
import { actions } from 'store/CleaningService';
import {
  selectCleaningServicePricing,
  selectOnboardingServiceStartDate,
  selectOnboardingStandardDay,
  selectOnboardingTouchUpDay
} from 'store/CleaningService/selectors';
import { selectServicesPaymentMethod } from 'store/Tenancy/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import CleaningServiceLegalText from './CleaningServiceLegalText';
import { calculateCleaningSchedule, calculateYearlyPriceAverage, getFirstOccurrenceOfWeekday } from './utils';
import { createCleaningService } from '../requests';

interface Props {}

const dayToName: Record<number, string> = {
  1: 'Mondays',
  2: 'Tuesdays',
  3: 'Wednesdays',
  4: 'Thursdays',
  5: 'Fridays'
};

const CleaningServiceCostSummarybox: React.FC<Props> = () => {
  const { isMobile } = useContext(media);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const pricing = useAppSelector(selectCleaningServicePricing);
  const servicesPaymentMethod = useAppSelector(selectServicesPaymentMethod);
  const touchUpDay = useAppSelector(selectOnboardingTouchUpDay);
  const [requestLoading, setRequestLoading] = useState(false);
  const standardDay = useAppSelector(selectOnboardingStandardDay);
  const serviceStartDate = useAppSelector(selectOnboardingServiceStartDate);

  if (!pricing || !serviceStartDate || !standardDay) {
    return null;
  }
  const touchUpCleaningPrice = pricing.touch_up_price;
  const standardCleaningPrice = pricing.cleaning_price;

  const touchupStartDate = getFirstOccurrenceOfWeekday(touchUpDay, serviceStartDate);
  const standardStartDate = getFirstOccurrenceOfWeekday(standardDay, serviceStartDate);
  const serviceStartDateMonth = DateTime.fromISO(serviceStartDate).monthShort;
  const cleaningSchedule = calculateCleaningSchedule(DateTime.fromISO(serviceStartDate), standardDay, touchUpDay);
  const firstMonthTotal =
    cleaningSchedule[0].numStandardCleanings * standardCleaningPrice +
    cleaningSchedule[0].numTouchUpCleanings * touchUpCleaningPrice;

  const yearlyPriceAverage = calculateYearlyPriceAverage(
    DateTime.fromISO(serviceStartDate),
    standardDay,
    touchUpDay,
    touchUpCleaningPrice,
    standardCleaningPrice
  );

  const handleStartService = async () => {
    setRequestLoading(true);
    if (!standardStartDate) {
      return;
    }

    try {
      const response = await createCleaningService({
        standardCleaningServiceStartDate: standardStartDate.toISO(),
        touchUpServiceStartDate: touchupStartDate?.toISO()
      });

      if (response.status === 201) {
        dispatch(actions.setSchedules(response.data));
        dispatch(actions.setOnboardingServiceStartDate(null));
        dispatch(actions.setOnboardingStandardDay(null));
        dispatch(actions.setOnboardingTouchUpDay(null));
        history.push(CLEANING_SERVICE_PATH);
      } else {
        showSnackbar({
          message: 'Error starting service. Please try again.',
          negative: true
        });
      }
    } catch (e) {
      showSnackbar({
        message: 'Error starting service. Please try again.',
        negative: true
      });
      console.error(e);
    }
    setRequestLoading(false);
  };

  if (!standardStartDate) {
    return null;
  }

  return (
    <Layout
      width={isMobile ? '100%' : 414}
      height={'fit-content'}
      padding={isMobile ? 24 : 36}
      color={isMobile ? 'white' : 'gray-25'}
      gap={24}
      direction="column"
      borderRadius={16}
    >
      <Text size="headline-sm" color="gray-900">
        Service summary
      </Text>
      <Layout direction="column" gap={16}>
        {touchUpDay && touchupStartDate && touchupStartDate < standardStartDate && (
          <Layout direction="column" gap={4}>
            <Layout direction="row" justify="space-between">
              <Text size="body-sm" semibold>
                Touch-up cleaning
              </Text>
              <Text size="body-sm" semibold>
                ${touchUpCleaningPrice} ea.
              </Text>
            </Layout>
            <Text size="body-xs" color="gray-400">
              {dayToName[touchUpDay]} starting {formatDate({ date: touchupStartDate ?? '', format: 'SHORTEST' })}
            </Text>
          </Layout>
        )}
        <Layout direction="column" gap={4}>
          <Layout direction="row" justify="space-between">
            <Text size="body-sm" semibold>
              Standard cleaning
            </Text>
            <Text size="body-sm" semibold>
              ${standardCleaningPrice} ea.
            </Text>
          </Layout>
          <Text size="body-xs" color="gray-400">
            {dayToName[standardDay]} starting {formatDate({ date: standardStartDate ?? '', format: 'SHORTEST' })}
          </Text>
        </Layout>
        {touchUpDay && touchupStartDate && touchupStartDate > standardStartDate && (
          <Layout direction="column" gap={4}>
            <Layout direction="row" justify="space-between">
              <Text size="body-sm" semibold>
                Touch-up cleaning
              </Text>
              <Text size="body-sm" semibold>
                ${touchUpCleaningPrice} ea.
              </Text>
            </Layout>
            <Text size="body-xs" color="gray-400">
              {dayToName[touchUpDay]} starting {formatDate({ date: touchupStartDate ?? '', format: 'SHORTEST' })}
            </Text>
          </Layout>
        )}
      </Layout>
      <Layout borderTop={true} borderWidth={2} color="gray-50"></Layout>
      <Layout direction="row" justify="space-between">
        <Text size="headline-xs" color="gray-900">
          Est. {serviceStartDateMonth} total
        </Text>
        <Text size="headline-xs">{formatUsd(firstMonthTotal)}</Text>
      </Layout>
      <Layout borderRadius={8} direction="column" padding={12} gap={12} color="purple-gradient-transparent">
        <Layout direction="row" justify="space-between">
          <Layout direction="row" gap={8} align="center">
            <Icon name="avg" size={'sm'} color="gray-900" stroke={2} />
            <Text size="body-sm" semibold>
              Avg. monthly
            </Text>
          </Layout>
          <Text size="body-sm" semibold>
            {formatUsd(yearlyPriceAverage)}
          </Text>
        </Layout>
        <Text size="body-xs" color="gray-400">
          Charges vary by number of services per month.
        </Text>
      </Layout>
      <Layout direction="column" gap={16}>
        <PillButton
          fullWidth
          type="purpleGradient"
          size="lg"
          text={'Start service'}
          onClick={handleStartService}
          disabled={requestLoading || !servicesPaymentMethod}
          showSpinner={requestLoading}
        />
        <CleaningServiceLegalText />
      </Layout>
    </Layout>
  );
};

export default CleaningServiceCostSummarybox;
